import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  LayoutDashboard,
  Building2,
  Truck,
  Users,
  FileText,
  Receipt,
  CreditCard,
  FileCheck,
  TrendingUp,
  Settings,
  LogOut,
  Menu,
  X,
  AlertTriangle,
  Bell,
  ShoppingCart,
  Package,
  Calculator,
  Mail,
  BookOpen,
  Landmark,
  PieChart,
  BarChart3,
  Shield,
  ClipboardList,
  PackageCheck,
  Wrench,
  Boxes,
  DollarSign,
  Sparkles,
  Briefcase,
  Wallet,
  UserCog,
  IdCard
} from 'lucide-react';
import { useState } from 'react';

// Menu item + roles allowed. Empty roles array = visible to everyone.
const navigation = [
  { name: 'My Tasks', href: '/dashboard', icon: LayoutDashboard, roles: [] },
  { name: 'My Sales', href: '/my-sales', icon: TrendingUp, roles: ['sales'] },
  { name: 'Job Assignments', href: '/job-assignments', icon: ClipboardList, roles: ['admin', 'operation_manager', 'operations', 'operation_executive', 'operation_coordinator'] },
  { name: 'Enquiries', href: '/enquiries', icon: FileText, roles: [] },
  { name: 'Quotations', href: '/quotations', icon: FileCheck, roles: [] },
  { name: 'Delivery Notes V2', href: '/delivery-notes-v2', icon: PackageCheck, roles: [] },
  { name: 'Drivers', href: '/drivers', icon: IdCard, roles: ['admin', 'operations', 'operation_manager', 'operation_executive', 'operation_coordinator'] },
  { name: 'Petty Cash', href: '/petty-cash', icon: Wallet, roles: [] },
  { name: 'Analytics', href: '/analytics', icon: TrendingUp, roles: ['admin', 'sales_manager', 'operations', 'finance'] },
  { name: 'AI Extraction', href: '/ai-extraction', icon: Sparkles, roles: ['admin', 'operations', 'finance'] },
  { name: 'Supplier Timesheets', href: '/supplier-timesheets', icon: Truck, roles: ['admin', 'operations', 'finance'] },
  { name: 'Companies', href: '/companies', icon: Building2, roles: ['admin'] },
  { name: 'Users', href: '/users', icon: UserCog, roles: ['admin'] },
  { name: 'Fleet', href: '/fleet', icon: Truck, roles: ['admin', 'operations', 'sales_manager'] },
  { name: 'Investor Pools', href: '/investor-pools', icon: Briefcase, roles: ['admin', 'finance'] },
  { name: 'Suppliers', href: '/suppliers', icon: Users, roles: ['admin', 'procurement', 'operations'] },
  { name: 'Purchase Orders', href: '/purchase-orders', icon: ShoppingCart, roles: ['admin', 'procurement', 'operations'] },
  { name: 'Client POs', href: '/client-pos', icon: ClipboardList, roles: ['admin', 'sales_manager', 'operations', 'sales'] },
  { name: 'Demobilization Requests', href: '/demob-requests', icon: PackageCheck, roles: ['sales'] },
  { name: 'Demobilization', href: '/demobilization', icon: PackageCheck, roles: ['admin', 'operations', 'operation_manager', 'operation_executive', 'operation_coordinator', 'finance', 'finance_manager'] },
  { name: 'Equipment & Manpower', href: '/equipment-manpower', icon: Wrench, roles: ['admin', 'operations', 'finance'] },
  { name: 'Material Module', href: '/material-module', icon: Boxes, roles: ['admin', 'operations'] },
  { name: 'Transportation', href: '/transportation', icon: Truck, roles: ['admin', 'operations'] },
  { name: 'Collections', href: '/collections', icon: DollarSign, roles: ['admin', 'finance'] },
  { name: 'Email Parser', href: '/email-parser', icon: Mail, roles: ['admin', 'operations'] },
  { name: 'Contracts', href: '/contracts', icon: Receipt, roles: ['admin', 'sales_manager', 'operations'] },
  { name: 'Timesheets', href: '/timesheets', icon: FileText, roles: ['admin', 'operations', 'finance'] },
  { name: 'Invoices', href: '/invoices', icon: Receipt, roles: ['admin', 'finance'] },
  { name: 'Core Accounting', href: '/accounting', icon: BookOpen, roles: ['admin', 'finance'] },
  { name: 'Asset Management', href: '/asset-management', icon: Landmark, roles: ['admin', 'finance'] },
  { name: 'Revenue & Cost', href: '/revenue-cost', icon: PieChart, roles: ['admin', 'finance'] },
  { name: 'Financial Dashboard', href: '/financial-dashboard', icon: BarChart3, roles: ['admin', 'finance'] },
  { name: 'Governance & Budget', href: '/governance', icon: Shield, roles: ['admin', 'finance'] },
  { name: 'Bank Reconciliation', href: '/bank-reconciliation', icon: Landmark, roles: ['admin', 'finance'] },
  { name: 'Finance', href: '/finance', icon: Calculator, roles: ['admin', 'finance'] },
  { name: 'Commissions', href: '/payments', icon: CreditCard, roles: ['admin', 'finance', 'sales_manager'] },
  { name: 'Documents', href: '/reports', icon: TrendingUp, roles: ['admin', 'operations'] },
  { name: 'Damage Reports', href: '/settings', icon: AlertTriangle, roles: ['admin', 'operations'] },
  { name: 'Follow-ups', href: '/follow-ups', icon: Bell, roles: ['admin', 'sales_manager', 'operations'] },
];

export const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logoutUser } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logoutUser();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-primary text-primary-foreground transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <h1 className="text-2xl font-black" data-testid="sidebar-logo">Nexus ERP</h1>
            <button
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
              data-testid="close-sidebar-button"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {navigation
              .filter((item) => !item.roles?.length || item.roles.includes(user?.role))
              .map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-white/10 text-white font-semibold'
                      : 'text-white/70 hover:bg-white/5 hover:text-white'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center justify-between mb-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate" data-testid="user-name">{user?.full_name}</p>
                <p className="text-xs text-white/60 truncate" data-testid="user-role">{user?.role}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-white/70 hover:text-white hover:bg-white/5"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="glass sticky top-0 z-30 flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 border-b">
          <button
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
            data-testid="open-sidebar-button"
          >
            <Menu className="h-6 w-6" />
          </button>
          <h1 className="lg:hidden font-black text-lg">Nexus ERP</h1>
          <div className="flex-1" />
        </header>

        {/* Page content — extra bottom padding on mobile so content isn't hidden behind bottom nav */}
        <main className="p-4 sm:p-6 pb-24 lg:pb-6">
          {children}
        </main>
      </div>

      {/* Mobile bottom navigation — visible on phones only */}
      <MobileBottomNav currentPath={location.pathname} />
    </div>
  );
};


// Mobile bottom nav — shown on <lg screens. Sales gets 4 tabs, others get 5 (adds Menu-more).
const MOBILE_BOTTOM_TABS = [
  { name: 'Enquiries', href: '/enquiries', icon: FileText, testid: 'mobile-nav-enquiries' },
  { name: 'Quotations', href: '/quotations', icon: FileCheck, testid: 'mobile-nav-quotations' },
  { name: 'Delivery', href: '/delivery-notes-v2', icon: PackageCheck, testid: 'mobile-nav-delivery' },
  { name: 'Tasks', href: '/dashboard', icon: LayoutDashboard, testid: 'mobile-nav-tasks' },
];

const MobileBottomNav = ({ currentPath }) => {
  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]"
      data-testid="mobile-bottom-nav"
    >
      <div className="grid grid-cols-4">
        {MOBILE_BOTTOM_TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive =
            currentPath === tab.href ||
            (tab.href !== '/dashboard' && currentPath.startsWith(tab.href));
          return (
            <Link
              key={tab.name}
              to={tab.href}
              data-testid={tab.testid}
              className={`flex flex-col items-center justify-center gap-1 py-2.5 text-xs font-medium transition-colors ${
                isActive
                  ? 'text-primary'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              <Icon className={`h-5 w-5 ${isActive ? 'stroke-[2.5]' : ''}`} />
              <span>{tab.name}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};
